import { CommerceContext, type CommerceContextOptions } from './CommerceContext.js'
import { CommerceHttpAdapter } from './CommerceHttpAdapter.js'
import { ConnectivityManager } from '../connectivity/ConnectivityManager.js'
import { WorkerBridge } from '../workers/WorkerBridge.js'
import { CacheStrategy } from '../workers/CacheStrategy.js'
import { SessionManagerFactory } from '../session/SessionManagerFactory.js'
import { SessionModule } from '../modules/session/index.js'
import { CartModule } from '../modules/cart/index.js'
import { CatalogModule } from '../modules/catalog/index.js'
import { ShippingModule } from '../modules/shipping/index.js'
import { MarketingModule } from '../modules/marketing/index.js'
import { StoresModule } from '../modules/stores/index.js'
import { PaymentsModule } from '../modules/payments/index.js'
import { DeviceManager } from '../kiosk/DeviceManager.js'
import type { SessionManager } from '../session/SessionManager.js'
import type { SessionInfo } from '../types/index.js'

export { CommerceContext, type CommerceContextOptions } from './CommerceContext.js'

/**
 * Point d'entrée principal du SDK Altazion Commerce.
 *
 * Usage :
 * ```ts
 * const client = new CommerceClient({ baseUrl: 'https://api.monsite.com', siteUrl: window.location.href })
 * await client.initialize()
 *
 * const cart = await client.cart.getCart()
 * ```
 */
export class CommerceClient {
  readonly context: CommerceContext
  readonly connectivity: ConnectivityManager

  readonly session: SessionModule
  readonly cart: CartModule
  readonly catalog: CatalogModule
  readonly shipping: ShippingModule
  readonly marketing: MarketingModule
  readonly stores: StoresModule
  readonly payments: PaymentsModule
  readonly devices: DeviceManager

  private readonly http: CommerceHttpAdapter
  private readonly workerBridge: WorkerBridge
  private readonly cacheStrategy: CacheStrategy
  private readonly sessionManager: SessionManager

  constructor(options: CommerceContextOptions) {
    this.context = new CommerceContext(options)
    this.http = new CommerceHttpAdapter(this.context)
    this.connectivity = new ConnectivityManager()
    this.workerBridge = new WorkerBridge()
    this.cacheStrategy = new CacheStrategy(this.workerBridge, this.http)
    this.sessionManager = SessionManagerFactory.create(this.http, this.context)
    this.devices = new DeviceManager()

    // Modules API
    this.session = new SessionModule(this.http, this.cacheStrategy)
    this.cart = new CartModule(this.http, this.cacheStrategy, this.connectivity, this.workerBridge)
    this.catalog = new CatalogModule(this.http, this.cacheStrategy)
    this.shipping = new ShippingModule(this.http, this.cacheStrategy)
    this.marketing = new MarketingModule(this.http, this.cacheStrategy)
    this.stores = new StoresModule(this.http, this.cacheStrategy)
    this.payments = new PaymentsModule(this.http)
  }

  /**
   * Initialise le SDK :
   * - Crée la session si nécessaire (mode browser uniquement)
   */
  async initialize(): Promise<SessionInfo | null> {
    return this.sessionManager.initialize()
  }

  /**
   * Retourne vrai si le SDK est hors-ligne.
   */
  get isOffline(): boolean {
    return this.connectivity.isOffline
  }

  /**
   * Force le vidage du cache.
   */
  clearCache(): Promise<void> {
    return this.workerBridge.clearAll()
  }

  /**
   * Libère les ressources (listeners, worker).
   */
  dispose(): void {
    this.connectivity.dispose()
    this.workerBridge.dispose()
    this.devices.dispose()
  }
}
